import type { AccountKind, AccountSummary, Me } from '@platform/shared';
import { api, getAccessToken, onTokenChange, refreshAccessToken, setAccessToken, setStaffSession, staffSession } from '../lib/api';
import { API_URL } from '../lib/config';
import { DESKTOP_QUERY } from '../lib/media';
import { detachPush } from '../lib/push';
import { createStore, useStore } from '../lib/store';
import { clearReferral, pendingReferral } from '../lib/referral';

interface SessionState {
  ready: boolean;
  me: Me | null;
  kind: AccountKind;
}

/**
 * The live account is the default: every login, new tab and app launch opens on it. A switch to demo
 * lasts for this tab only (sessionStorage, so a reload keeps it), and only on a computer (DESKTOP_QUERY).
 */
const KIND_KEY = 'accountKind';
const desktop = () => typeof window === 'undefined' || window.matchMedia(DESKTOP_QUERY).matches;

export const sessionStore = createStore<SessionState>({ ready: false, me: null, kind: 'live' });

/** Live, unless this tab switched to demo on a computer and the user has a demo account. */
function fitKind(me: Me | null): AccountKind {
  const kinds = me?.accounts.map((a) => a.kind) ?? [];
  let saved: string | null = null;
  try {
    saved = sessionStorage.getItem(KIND_KEY);
  } catch {
    /* ignore */
  }
  if (saved === 'demo' && desktop() && kinds.includes('demo')) return 'demo';
  return kinds.length && !kinds.includes('live') ? kinds[0]! : 'live';
}

export async function loadMe() {
  const me = await api.get<Me>('/api/me');
  sessionStore.set({ me, kind: fitKind(me) });
  return me;
}

export async function bootstrapSession() {
  try {
    const t = await refreshAccessToken();
    if (t) await loadMe();
  } catch {
    setAccessToken(null);
  } finally {
    sessionStore.set({ ready: true });
  }
}

/** Logged in, or (temporary password set by support) sent to choose a new password first. */
type SessionResult = { accessToken: string } | { passwordChangeRequired: true; resetToken: string };

/** A staff Telegram code (STAFF_TELEGRAM_PLAN.md): where it went, or why it couldn't be sent. */
export interface CodeDelivery {
  sent: boolean;
  to?: string;
  resendIn?: number;
  error?: string;
  message?: string;
}

export type LoginResult =
  | { otpRequired: true; challengeId: string; method: 'email' | 'totp'; devCode?: string; telegramAvailable?: boolean; resendIn?: number }
  | { otpRequired: true; challengeId: string; method: 'telegram'; telegram: CodeDelivery; authenticatorFallback: boolean }
  | ({ otpRequired: false } & SessionResult);

/** Where to send the user after login when their password must be replaced, or null. */
export const passwordChangePath = (r: object): string | null =>
  'resetToken' in r && typeof r.resetToken === 'string' ? `/reset-password?token=${encodeURIComponent(r.resetToken)}&required=1` : null;

async function startSession(r: SessionResult) {
  if (!('accessToken' in r)) return;
  forgetKind();
  setAccessToken(r.accessToken);
  await loadMe();
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const r = await api.post<LoginResult>('/api/auth/login', { email, password });
  if (!r.otpRequired) await startSession(r);
  return r;
}

/** Where to land after signing in: a dealer always goes to the dealer desk; a new sign-up with steps left goes to them unless a page was asked for. */
export function landingPage(next: string): string {
  const me = sessionStore.get().me;
  if (me?.role === 'dealer') return '/admin';
  return me && me.onboardingStep !== 'done' && next === '/' ? '/onboarding' : next;
}

/** Returns the page to go to instead of `next` when the password must be replaced first. */
export async function verifyOtp(challengeId: string, code: string): Promise<string | null> {
  const r = await api.post<SessionResult>('/api/auth/verify-otp', { challengeId, code });
  await startSession(r);
  return passwordChangePath(r);
}

/**
 * Log in as client (staff, from the CRM): redeem the single-use link. The session belongs to this tab
 * (staffSession in lib/api.ts): the browser's own session is left alone. Opens the live account.
 */
export async function crmLogin(token: string) {
  try {
    sessionStore.set({ me: null });
    const r = await api.post<{ accessToken: string; refreshToken: string; expiresAt: string; signedInBy: string }>('/api/auth/crm-login', { token });
    setStaffSession({ refreshToken: r.refreshToken, expiresAt: r.expiresAt, signedInBy: r.signedInBy });
    setAccessToken(r.accessToken);
    const me = await loadMe();
    if (me.accounts.some((a) => a.kind === 'live')) sessionStore.set({ kind: 'live' });
  } finally {
    sessionStore.set({ ready: true });
  }
}

/** "Open Platform" in the CRM: redeem the single-use link for the staff member's own login (an ordinary session). */
export async function staffLinkLogin(token: string) {
  try {
    sessionStore.set({ me: null });
    const r = await api.post<{ accessToken: string }>('/api/auth/staff-link', { token });
    await startSession(r);
  } finally {
    sessionStore.set({ ready: true });
  }
}

/** The user an access token is for (JWT `sub`), or null. */
function tokenSubject(t: string): string | null {
  try {
    const p = JSON.parse(atob(t.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: unknown };
    return typeof p.sub === 'string' ? p.sub : null;
  } catch {
    return null;
  }
}

// The refresh cookie is shared by every tab. When another tab signs in as someone else (staff opening
// a second client from the CRM), this tab's next refresh would carry on as that user: start over instead.
onTokenChange((t) => {
  const me = sessionStore.get().me;
  if (t && me && tokenSubject(t) !== me.id) window.location.reload();
});

export async function register(email: string, password: string, ref = pendingReferral()) {
  await api.post('/api/auth/register', { email, password, ref });
  clearReferral();
}

export async function logout() {
  const staff = staffSession();
  try {
    // While the session still works: this device stops getting the client's alerts. A staff session
    // never subscribed, and sends its own token so the browser's cookie session is left alone.
    if (!staff) await detachPush();
    await api.post('/api/auth/logout', staff ? { refreshToken: staff.refreshToken } : {});
  } finally {
    setStaffSession(null);
    setAccessToken(null);
    sessionStore.set({ me: null });
    forgetKind();
  }
}

// Closing the tab (or leaving Platform) ends a staff session: tell the API the tab is going, in the
// background. A reload is back within seconds and keeps the session; otherwise the API's worker ends it.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    const t = getAccessToken();
    if (!t || !staffSession()) return;
    try {
      void fetch(`${API_URL}/api/auth/staff-leaving`, { method: 'POST', keepalive: true, headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
    } catch {
      /* leaving anyway */
    }
  });
}

/** Demo works on a computer only: a switch to it is ignored below DESKTOP_QUERY. */
export function setAccountKind(kind: AccountKind) {
  const me = sessionStore.get().me;
  if (me && !me.accounts.some((a) => a.kind === kind)) return;
  if (kind === 'demo' && !desktop()) return;
  try {
    sessionStorage.setItem(KIND_KEY, kind);
  } catch {
    /* ignore */
  }
  sessionStore.set({ kind });
}

function forgetKind() {
  try {
    sessionStorage.removeItem(KIND_KEY);
    localStorage.removeItem(KIND_KEY); // the old, remembered-forever choice
  } catch {
    /* ignore */
  }
  sessionStore.set({ kind: 'live' });
}

// A window made narrower than a computer screen while on demo goes back to live.
if (typeof window !== 'undefined') {
  window.matchMedia(DESKTOP_QUERY).addEventListener('change', (e) => {
    if (!e.matches && sessionStore.get().kind === 'demo') sessionStore.set({ kind: 'live' });
  });
}

/** Replace one account summary (from WS balance events or REST refresh). */
export function updateAccount(acc: AccountSummary) {
  sessionStore.set((s) => (s.me ? { me: { ...s.me, accounts: s.me.accounts.map((a) => (a.id === acc.id ? acc : a)) } } : {}));
}

export const useSession = () => useStore(sessionStore, (s) => s);
export const useMe = () => useStore(sessionStore, (s) => s.me);
export const useAccountKind = () => useStore(sessionStore, (s) => s.kind);
export const useActiveAccount = () =>
  useStore(sessionStore, (s) => s.me?.accounts.find((a) => a.kind === s.kind) ?? null);
